/**
 * Card view component for a team member.
 * Shows avatar, name, email, role, join date, listings, and action menu.
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { TeamMember } from '../../types/company';
import RoleBadge from './RoleBadge';

interface TeamMemberCardProps {
  member: TeamMember;
  onRoleChange?: (member: TeamMember) => void;
  onRemove?: (member: TeamMember) => void;
  isCurrentUser?: boolean;
}

/**
 * Format date for display.
 */
function formatDate(dateString: string, locale: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}

/**
 * Generate initials from display name.
 */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

export default function TeamMemberCard({
  member,
  onRoleChange,
  onRemove,
  isCurrentUser = false,
}: TeamMemberCardProps) {
  const { t, i18n } = useTranslation('team');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Hide menu for OWNER role or current user
  const showMenu = member.role !== 'OWNER' && !isCurrentUser;

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen]);

  const handleRoleChange = () => {
    setMenuOpen(false);
    onRoleChange?.(member);
  };

  const handleRemove = () => {
    setMenuOpen(false);
    onRemove?.(member);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-4 relative">
      {/* Action Menu Button */}
      {showMenu && (
        <div className="absolute top-3 end-3" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Member actions"
          >
            <i className="ri-more-2-fill text-lg" />
          </button>

          {/* Dropdown Menu */}
          {menuOpen && (
            <div className="absolute end-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
              <button
                onClick={handleRoleChange}
                className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-start flex items-center gap-2"
              >
                <i className="ri-user-settings-line" />
                {t('changeRole', 'Change Role')}
              </button>
              <button
                onClick={handleRemove}
                className="w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 text-start flex items-center gap-2"
              >
                <i className="ri-user-unfollow-line" />
                {t('removeMember', 'Remove Member')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Avatar */}
      <div className="flex items-center mb-3">
        {member.avatarUrl ? (
          <img
            src={member.avatarUrl}
            alt={member.displayName}
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
            <span className="text-blue-700 font-semibold text-sm">
              {getInitials(member.displayName)}
            </span>
          </div>
        )}
      </div>

      {/* Name and Email */}
      <h4 className="font-medium text-gray-900 truncate">{member.displayName}</h4>
      <p className="text-sm text-gray-500 truncate mb-2">{member.email}</p>

      {/* Role Badge */}
      <div className="mb-3">
        <RoleBadge role={member.role} />
      </div>

      {/* Secondary Info */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>
          {t('memberSince', 'Member since')} {formatDate(member.joinedAt, i18n.language)}
        </span>
        <span>
          {member.totalListings} {t('listings', 'listings')}
        </span>
      </div>
    </div>
  );
}
